import invariant from 'tiny-invariant';

import { createDraftOrder, getCart, getCurrentLocale } from '../api';
import { getFilteredAddedOffers } from '../contexts/products';
import { logInfo, logError } from '../utils/logging';

export function isVisible(element) {
  return !!(
    element.offsetWidth ||
    element.offsetHeight ||
    element.getClientRects().length
  );
}

function customIgnoreQuery(element) {
  if (!window.GiftBox.customIgnoreButtonSelector) return true;
  try {
    return !element.matches(window.GiftBox.customIgnoreButtonSelector);
  } catch (error) {
    logError('customIgnoreButtonSelector is not a valid selector', error);
  }
}

function getButtons() {
  window.GiftBox.customButtonSelectors =
    window.GiftBox.customButtonSelectors ||
    window.GIFTBOX_CUSTOM_BUTTON_SELECTORS ||
    [];

  const selectors = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'form[action^="/checkout"] [type="submit"]',
    '[href*="checkout"]',
    'button[value="Checkout"]',
    'input[value="Checkout"]',
  ]
    .concat(window.GiftBox.customButtonSelectors)
    .map((selector) => `${selector}:not(.giftbox-checkout-cloned)`)
    .join(',');
  return Array.from(document.querySelectorAll(selectors))
    .concat(
      Array.from(
        document.querySelectorAll(
          '[onclick*="checkout"]:not(.giftbox-checkout-cloned)',
        ),
      ).filter((element) =>
        element.attributes.onclick.value.match(/location.*checkout/),
      ),
    )
    .filter(
      (element) =>
        !buttonPairs.find(
          (pair) =>
            pair.hiddenButton === element || pair.clonedButton === element,
        ),
    )
    .filter(
      (element) => !Array.from(element.classList).includes('partiallyButton'),
    )
    .filter(customIgnoreQuery)
    .filter(isVisible);
}

const buttonPairs = [];

function hideButton(button) {
  button.setAttribute(
    'style',
    'display:none!important;visibility:hidden;position:absolute;left:-10000px;',
  );
}

function getAttributes() {
  const attributesElements = document.querySelectorAll('[name^="attributes"]');
  const attributes = {};

  for (const attributesElement of attributesElements) {
    if (!attributesElement.name) continue;
    const accessor = attributesElement.name.match(/\[(.*)\]/)[1];
    const isCheckbox = attributesElement.type === 'checkbox';
    let { value } = attributesElement;

    if (isCheckbox) {
      value = attributesElement.checked ? value : null;
    }

    if (value !== null) {
      attributes[accessor] = attributesElement.value;
    }
  }

  return attributes;
}

export function cleanupUnpairedButtons() {
  try {
    const clonedButton = document.querySelectorAll('.giftbox-checkout-cloned');
    clonedButton.forEach((button) => {
      const buttonPair = buttonPairs.find(
        (pair) => pair.clonedButton === clonedButton,
      );
      if (!buttonPair) {
        logInfo('Removing unpaired button', button);
        button.classList.remove('giftbox-checkout');
        button.classList.remove('giftbox-checkout-cloned');
      }
    });
  } catch (error) {
    logError(error);
  }
}

export function cloneButtons() {
  const buttons = getButtons();
  logInfo(`found ${buttons.length} buttons`);

  buttons.forEach((button) => {
    button.classList.add('giftbox-checkout');
    const cloned = button.cloneNode(true);
    cloned.onclick = null;
    cloned.classList.add('giftbox-checkout-cloned');
    button.insertAdjacentElement('afterend', cloned);
    hideButton(button);
    watchButtonRemoval(button);
    watchButtonChanges(button);

    buttonPairs.push({
      clonedButton: cloned,
      hiddenButton: button,
    });
  });

  buttonPairs.forEach(({ clonedButton, hiddenButton }) => {
    clonedButton.addEventListener(
      'click',
      async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        try {
          if (window.GIFTBOX_CAN_CHECKOUT && !window.GIFTBOX_CAN_CHECKOUT()) {
            logInfo('GIFTBOX_CAN_CHECKOUT prevented checkout');
            return;
          }
        } catch (error) {
          logError('GIFTBOX_CAN_CHECKOUT check failed', error);
        }

        try {
          // Button needs to be re-enabled before redirects due to Safari cache issues
          event.target.disabled = true;
          const offers = getFilteredAddedOffers();
          if (!offers || offers.length === 0) {
            logInfo('No offers added using normal checkout...');
            if (window.$ && !(hiddenButton instanceof HTMLAnchorElement)) {
              logInfo('jQuery found. Clicking using jQuery.');
              try {
                event.target.disabled = false;
                window.$(hiddenButton).trigger('click');
              } catch (error) {
                logError(
                  'jQuery click failed. Falling back to .click()',
                  error,
                );
                event.target.disabled = false;
                hiddenButton.click();
              }
            } else {
              event.target.disabled = false;
              hiddenButton.click();
            }
            event.target.disabled = false;
            return;
          }
          const cart = await getCart();
          cart.attributes = {
            ...cart.attributes,
            ...getAttributes(),
          };
          const noteElement = document.querySelector('[name=note]');
          if (noteElement && noteElement.value) {
            cart.note = noteElement.value;
          }
          invariant(cart.items.length > 0, `Cart is empty.`);
          const draftOrder = await createDraftOrder(
            window.Shopify.shop,
            cart,
            offers,
            window.giftBoxShippingAddress,
          );
          logInfo(draftOrder);
          const shopLocale = window.GIFTBOX_LOCALE
            ? window.GIFTBOX_LOCALE
            : getCurrentLocale();
          event.target.disabled = false;
          window.location.href =
            draftOrder.invoice_url +
            (shopLocale ? `?locale=${shopLocale}` : '');
        } catch (error) {
          logError('Draft order creation failed', error);
          event.target.disabled = false;
          hiddenButton.click();
        }
      },
      { capture: true },
    );
  });
}

function getPairByHiddenButton(button) {
  return buttonPairs.find((pair) => pair.hiddenButton === button);
}

function watchButtonRemoval(targetNode) {
  let target = targetNode;
  function findInsertedButton(addedNodes, hiddenButton) {
    return Array.from(addedNodes).find(
      (n) =>
        n.nodeName === hiddenButton.nodeName && n.type === hiddenButton.type,
    );
  }

  const callback = function (mutationsList) {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        for (var node of mutation.removedNodes) {
          if (node === target) {
            logInfo(`${node.id} is being removed. Updating reference.`);
            const buttonPair = getPairByHiddenButton(target);
            buttonPair.hiddenButton = findInsertedButton(
              mutationsList.flatMap((e) => Array.from(e.addedNodes)),
              buttonPair.hiddenButton,
            );
            target = buttonPair.hiddenButton;
            hideButton(target);
          }
        }
      }
    }
  };

  try {
    const observer = new MutationObserver(callback);

    observer.observe(targetNode.parentElement, { childList: true });
  } catch (error) {
    logError('Failed to setup button observer', error);
  }
}

function watchButtonChanges(targetNode) {
  const callback = function () {
    logInfo(`${targetNode.id} is being modified. Updating content.`);
    const { clonedButton, hiddenButton } = getPairByHiddenButton(targetNode);
    if (hiddenButton.style.display !== 'none') {
      hideButton(hiddenButton);
    }
    clonedButton.disabled = hiddenButton.disabled;
    clonedButton.className = hiddenButton.className;
  };

  try {
    const observer = new MutationObserver(callback);

    observer.observe(targetNode, {
      childList: true,
      attributes: true,
      subtree: true,
    });
  } catch (error) {
    logError('Failed to setup button observer', error);
  }
}

export function getHiddenButton(clonedButton) {
  const buttonPair = buttonPairs.find(
    (pair) => pair.clonedButton === clonedButton,
  );
  invariant(buttonPair, `Hidden button couldn't be found for: ${clonedButton}`);
  return buttonPair.hiddenButton;
}
